// src/pages/ProductsServices.jsx
import React, { useLayoutEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import { jsonLdWithMedia } from "../seo/mediaSchema";
import LazyLoopClip from "../components/LazyLoopClip";

const industries = [
  "Automotive Assembly",
  "Warehousing & 3PL",
  "Electronics",
  "Food & Beverage",
  "General Manufacturing",
  "Aftermarket & Service",
];

const processSteps = [
  {
    n: "01",
    short: "Engineer",
    title: "Automation Solutions Engineering (ASE)",
    items: [
      {
        id: "engineer-plant",
        label:
          "Layouts, mechanical engineering, and fleet routing designed to the plant's floor conditions, aisle widths, and existing traffic patterns.",
      },
      {
        id: "engineer-safety",
        label: "Hardware standards, safety zoning, and cycle time analysis.",
      },
      {
        id: "engineer-constraints",
        label: "Plant constraints translated into a production-ready system design.",
      },
      {
        id: "engineer-docs",
        label: "Documentation, schematics, and traceability for a clean handoff to launch.",
      },
    ],
  },
  {
    n: "02",
    short: "Integrate",
    title: "Automation Technology & Integration (ATI)",
    items: [
      {
        id: "integrate-systems",
        label: "IT and OT network alignment with plant IT, MES and ERP, and security.",
      },
      {
        id: "integrate-traffic",
        label: "Fleet management software, middleware, PLC controls, and traffic interfaces.",
      },
      {
        id: "integrate-startup",
        label: "Turnkey installation, sensor calibration, and commissioning.",
      },
      {
        id: "integrate-train",
        label: "Hands-on training for plant IT, engineering, and maintenance teams.",
      },
    ],
  },
  {
    n: "03",
    short: "Support",
    title: "Automation Maintenance & Support (AMS)",
    items: [
      {
        id: "support-team",
        label: "24/7 on-site and remote coverage for the fleet and the systems it depends on.",
      },
      {
        id: "support-fixes",
        label: "Rapid diagnostics and fault resolution across fleet, FMS, network, and hardware.",
      },
      {
        id: "support-health",
        label: "Preventative maintenance: health checks, firmware updates, and hardware upkeep.",
      },
      {
        id: "support-telemetry",
        label:
          "Fleet telemetry and performance monitoring, with tuning of routes and utilization after launch.",
      },
    ],
  },
];

function StepArrow() {
  return (
    <span
      className="absolute -right-3 top-1/2 hidden -translate-y-1/2 text-gray-300 sm:block"
      aria-hidden="true"
    >
      <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
        <path
          d="M2.5 8h11M9.5 4l4 4-4 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="square"
        />
      </svg>
    </span>
  );
}

function SolutionsProcess() {
  return (
    <div className="grid gap-3 sm:grid-cols-3 sm:gap-6">
      {processSteps.map((step, index) => (
        <article key={step.n} className="flex flex-col">
          <div className="relative bg-gray-900 px-4 py-3 text-white">
            <p className="text-[10px] font-semibold tabular-nums tracking-[0.18em] text-white/55">
              {step.n}
            </p>
            <h3 className="mt-0.5 text-sm font-semibold sm:text-base">{step.short}</h3>
            {index < processSteps.length - 1 ? <StepArrow /> : null}
          </div>
          <div className="flex-1 bg-gray-50 px-4 py-4">
            <p className="text-sm font-semibold leading-snug text-gray-900">{step.title}</p>
            <ul className="mt-3 space-y-2.5">
              {step.items.map((item) => (
                <li key={item.id}>
                  <p className="text-sm leading-snug text-gray-700">{item.label}</p>
                </li>
              ))}
            </ul>
          </div>
        </article>
      ))}
    </div>
  );
}

function SolutionsHeroClip() {
  return (
    <figure className="relative">
      <LazyLoopClip
        src="/videos/linkedin-video-447kbps.mp4"
        poster="/images/linkedin-video-447kbps.jpg"
        label="MAG Lab integration footage"
        width={1280}
        height={720}
        wrapperClassName="relative aspect-video overflow-hidden bg-black"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <figcaption className="mt-3 flex items-center justify-between text-[11px] font-medium tracking-wide text-gray-500">
        <span>MAG Lab</span>
        <span className="tabular-nums">Livonia, MI</span>
      </figcaption>
    </figure>
  );
}

export default function ProductsServices() {
  const [expandedIndex, setExpandedIndex] = useState(null);

  useLayoutEffect(() => {
    if (typeof window === "undefined" || window.location.hash !== "#services") {
      return undefined;
    }
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}`
    );
    window.scrollTo(0, 0);
    return undefined;
  }, []);

  const faqs = [
    {
      q: "Our leadership team wants to implement automation as soon as possible. What is the best way to get started?",
      a: "Email solutions@mag-automate.com. The engagement team will connect the plant with an Automation Solutions Engineer who has worked in the same industry, and that engineer will work through the current process and the goals behind the project.",
    },
    {
      q: "My company just purchased 20 AGV's because they had the best sales and marketing presentation at the trade show, how can I integrate all of them?",
      a: "How did you come up with 20 robots? Are the processes defined from pickup to drop-off, and everything in between? What is triggering the orders? Have modifications within the facility been defined? Is the plant team aligned with the project? What is the plan for scaling with your current model? MAG can help answer all these questions and make the right decision for your facility. ",
    },
    {
      q: "Our team is new to automation and does not have enough internal support. How can MAG help?",
      a: "MAG can place an automation solutions engineer alongside the person or team leading the project to support decision making and internal engineering processes. Each engineer brings prior AGV and AMR experience to help guide the project.",
    },
    {
      q: "We are looking for someone to implement an AMR project at our facility. What does MAG do?",
      a: "As an integrator that specializes in AMR and AGV integration, we will work with you to define the scope of the work, select the AMR vendor and the fleet management system, align stakeholders across the plant, purchase the robots, commission the system, and define the strategy for long term support.",
    },
  ];

  return (
    <div className="bg-white text-gray-800">
      <Seo
        title="AGV & AMR Integration Services, Scope to Support | MAG"
        description="AMR and AGV systems are engineered, integrated, launched, and supported end to end by three in-house teams. One engagement, no handoffs."
        path="/products-services"
        image="https://www.mag-automate.com/images/Image.jpg"
        jsonLd={jsonLdWithMedia(
          "/products-services",
          breadcrumbList([
            { name: "Home", path: "/" },
            { name: "Products & Services", path: "/products-services" },
          ])
        )}
      />
      <section className="bg-white" aria-label="Products and Services hero">
        <div className="px-4 pt-4 pb-8 sm:px-6 sm:pt-5 lg:px-8 lg:pb-10">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-10">
            <div className="lg:col-span-5">
              <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl lg:leading-[1.05]">
                Turnkey AGV & AMR Integration Services
              </h1>
              <p className="mt-3 text-base leading-relaxed text-gray-500 sm:text-lg">
                End-to-end execution backed by direct OEM relationships, technical mastery, and automotive-grade floor experience.
              </p>
              <p className="mt-1 text-sm text-gray-400">
                Three dedicated in-house teams. Metro Detroit HQ.
              </p>
            </div>
            <div className="lg:col-span-7">
              <SolutionsHeroClip />
            </div>
          </div>
          <div className="mt-8">
            <SolutionsProcess />
            <div className="mt-6">
              <Link to="/contact" className="btn-primary-dark">
                Start with a Free PoC
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section
        id="industries"
        className="relative scroll-mt-24 overflow-hidden bg-gray-950 text-white"
      >
        <div className="absolute inset-0 bg-tech-grid opacity-30" aria-hidden="true" />
        <div className="relative px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
            Markets we Serve
          </p>
          <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-lg font-semibold tracking-tight text-white/90 sm:text-xl">
            {industries.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
          <h2 className="mt-12 flex flex-wrap items-baseline gap-x-3 text-3xl font-bold tracking-tight sm:text-4xl">
            <span>Transform Your Workplace using AI</span>
            <span className="text-base font-medium tracking-normal text-gray-400 sm:text-lg">
              (AMR Integration)
            </span>
          </h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
            See what all the hype in AI is about
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn-primary">
              Customer Impact
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-gray-200 px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Equipment in the fleet</h2>
        <Link to="/catalog" className="btn-primary-dark mt-8 px-8 py-4 text-base sm:text-lg">
          Explore our Catalog
        </Link>
      </section>

      <section className="px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">FAQs</h2>
        <div className="mt-6 w-full divide-y divide-gray-200 border border-gray-200 bg-white">
          {faqs.map((f, i) => {
            const open = expandedIndex === `faq-${i}`;
            return (
              <div key={i}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-5 py-4 text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
                  aria-expanded={open}
                  onClick={() =>
                    setExpandedIndex((prev) => (prev === `faq-${i}` ? null : `faq-${i}`))
                  }
                >
                  <span className="font-semibold text-gray-900">{f.q}</span>
                  <span className="ml-4 text-gray-500">{open ? "−" : "+"}</span>
                </button>
                <p
                  className={`px-5 text-sm leading-relaxed text-gray-600 ${
                    open ? "max-h-none pb-4 opacity-100" : "max-h-0 overflow-hidden pb-0 opacity-0"
                  }`}
                >
                  {f.a}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Structured data (optional) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Industrial Automation & AMR Integration",
            provider: { "@type": "Organization", name: "MAG" },
            areaServed: "North America",
            serviceType: processSteps.map((s) => s.title),
          }),
        }}
      />
    </div>
  );
}
